import { useEffect, useState } from 'react';
import { supabase, withCache, optimizedQuery, clearQueryCache } from '../lib/supabase';

interface Product {
  id: string;
  name: string;
  description: string;
  base_price: number;
  originalPrice?: number;
  main_image: string;
  stock: number;
  discount?: number;
  rating?: number;
  sales?: number;
  price?: number;
  code?: string;
}

interface Collection {
  id: string;
  name: string;
  description: string;
  type: 'category_mix' | 'new' | 'featured' | 'sale';
  products: Product[];
}

export function useCollection(type: Collection['type']) {
  const [collection, setCollection] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCollection = async () => {
      try {
        setLoading(true);
        
        // Clave de caché para la colección
        const cacheKey = `collection_${type}`;
        
        // Función para obtener la colección
        const getCollectionData = async () => {
          // Obtener la colección usando optimizedQuery
          const { data: collections, error: collectionError } = await optimizedQuery('product_collections', {
            select: 'id, name, description, type, image',
            filters: { is_active: true },
            orderBy: { column: 'order', ascending: true },
            useCache: true,
            cacheTTL: 300 // 5 minutos de caché
          });

          if (collectionError) {
            console.error('Error fetching collections:', collectionError);
            throw collectionError;
          }

          // Filtrar manualmente la colección por tipo
          return collections?.find((c: any) => c.type === type);
        };
        
        // Usar caché para la colección
        const collectionData = await withCache(
          cacheKey,
          getCollectionData,
          300 // 5 minutos 
        );

        if (!collectionData && type !== 'new') {
          console.log(`No collection found for type: ${type}`);
          setCollection(null);
          setLoading(false);
          return;
        }

        let products: Product[] = [];

        if (type === 'new') {
          // Para productos nuevos, optimizamos la consulta
          const newProductsCacheKey = 'new_products';
          
          const fetchNewProducts = async () => {
            const { data, error: productsError } = await optimizedQuery('products', {
              select: 'id, name, description, base_price, main_image, stock, code',
              filters: { status: 'active' },
              orderBy: { column: 'created_at', ascending: false },
              limit: 10,
              useCache: true,
              cacheTTL: 180 // 3 minutos de caché
            });

            if (productsError) {
              console.error('Error fetching new products:', productsError);
              throw productsError;
            }
            
            return data;
          };
          
          const newProducts = await withCache(
            newProductsCacheKey,
            fetchNewProducts,
            180 // 3 minutos
          );

          // Transformar los productos
          products = newProducts.map((product: any) => ({
            id: product.id,
            name: product.name,
            description: product.description,
            base_price: product.base_price,
            main_image: product.main_image,
            stock: product.stock,
            originalPrice: product.base_price * 1.4,
            discount: Math.floor(Math.random() * 30) + 30,
            rating: Number((Math.random() * (5 - 4) + 4).toFixed(1)),
            sales: Math.floor(Math.random() * 5000) + 500,
            price: product.base_price,
            code: product.code,
          }));
        } else if (collectionData) {
          // Para otros tipos, optimizamos las consultas en dos pasos
          const collectionProductsCacheKey = `collection_products_${collectionData.id}`;
          
          const fetchCollectionProducts = async () => {
            // Obtener IDs de productos con optimizedQuery
            const { data, error: idsError } = await optimizedQuery('collection_products', {
              select: 'product_id',
              filters: { collection_id: collectionData.id },
              useCache: true,
              cacheTTL: 300 // 5 minutos
            });

            if (idsError) {
              console.error('Error fetching collection product IDs:', idsError);
              throw idsError;
            }
            
            return data;
          };
          
          const collectionProductIds = await withCache(
            collectionProductsCacheKey,
            fetchCollectionProducts,
            300 // 5 minutos
          );

          if (collectionProductIds && collectionProductIds.length > 0) {
            // Extraer los IDs de productos
            const productIds = collectionProductIds.map((item: any) => item.product_id);
            
            // Clave de caché para los detalles de productos
            const productDetailsCacheKey = `product_details_${productIds.join('_')}`;
            
            const fetchProductDetails = async () => {
              // Obtener detalles de productos con optimizedQuery
              const { data, error: detailsError } = await optimizedQuery('products', {
                select: 'id, name, description, base_price, main_image, stock, code',
                filters: { id: { in: productIds }, status: 'active' },
                useCache: true,
                cacheTTL: 180 // 3 minutos
              });

              if (detailsError) {
                console.error('Error fetching product details:', detailsError);
                throw detailsError;
              }
              
              return data;
            };
            
            const productDetails = await withCache(
              productDetailsCacheKey,
              fetchProductDetails,
              180 // 3 minutos
            );

            // Transformar los productos
            products = productDetails.map((product: any) => ({
              id: product.id,
              name: product.name,
              description: product.description,
              base_price: product.base_price,
              main_image: product.main_image,
              stock: product.stock,
              originalPrice: product.base_price * 1.4,
              discount: Math.floor(Math.random() * 30) + 30,
              rating: Number((Math.random() * (5 - 4) + 4).toFixed(1)),
              sales: Math.floor(Math.random() * 5000) + 500,
              price: product.base_price,
              code: product.code,
            }));
          }
        }

        // Crear el objeto de colección
        setCollection({
          id: collectionData?.id || `${type}-default`,
          name: collectionData?.name || (type === 'new' ? 'Productos Nuevos' : 
                                        type === 'featured' ? 'Productos Destacados' : 
                                        type === 'sale' ? 'Ofertas Especiales' : 
                                        'Colección por Categoría'),
          description: collectionData?.description || 'Descubre nuestra selección especial de productos',
          type: type,
          products: products,
        });
      } catch (err) {
        console.error('Error in useCollection hook:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar la colección');
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();

    // Removido: suscripciones en tiempo real que consumen excesivo egress
    // Las colecciones se actualizan mediante caché con TTL
    return () => {
      // Cleanup function sin suscripciones
    };
  }, [type]);

  return { collection, loading, error };
} 